<template>
  <div class="video-place-container">
    <!-- 顶部筛选栏 -->
    <div class="filter-bar">
      <el-input
        v-model="keyword"
        prefix-icon="el-icon-search"
        placeholder="请输入视频名称"
        size="small"
        clearable
        class="search-input"
        @keyup.enter.native="handleSearch"
        @clear="handleSearch"
      ></el-input>
      <treeselect
        v-model="activeCategory"
        :options="categoryOptions"
        :normalizer="categoryNormalizer"
        :clearable="true"
        :default-expand-level="1"
        class="category-select"
        placeholder="全部分类"
        @select="handleCategoryChange"
      />
      <el-button
        type="primary"
        size="small"
        icon="el-icon-search"
        class="search-btn"
        @click="handleSearch"
      >搜索</el-button>
    </div>

    <!-- 视频卡片列表 -->
    <div class="video-grid" v-loading="loading">
      <div
        v-for="item in videoList"
        :key="item.id"
        class="video-card"
        @click="handlePlay(item)"
      >
        <div
          class="video-thumb"
          :class="{ 'has-cover': item.coverUrl }"
          :style="item.coverUrl ? { backgroundImage: `url(${resolveCoverUrl(item.coverUrl)})` } : {}"
        >
          <img v-if="item.coverUrl" :src="resolveCoverUrl(item.coverUrl)" class="thumb-image" alt="课程封面" />
          <div v-else class="play-circle">
            <div class="play-circle-ring"></div>
            <i class="el-icon-video-play play-icon"></i>
          </div>
        </div>
        <div class="video-info">
          <div class="video-title" :title="item.title">{{ item.title }}</div>
          <div class="video-meta">
            <el-tag size="mini" type="info" class="category-tag">{{ item.categoryName }}</el-tag>
            <span class="instructor">{{ item.instructor }}</span>
          </div>
          <div v-if="item.description" class="video-desc" :title="item.description">
            {{ item.description }}
          </div>
        </div>
      </div>
      <div v-if="!loading && videoList.length === 0" class="empty-tip">
        <i class="el-icon-document"></i>
        <p>暂无视频数据</p>
      </div>
    </div>

    <!-- 分页 -->
    <div class="pagination-wrap">
      <span class="pagination-info">共 {{ total }} 条</span>
      <el-pagination
        background
        layout="sizes, prev, pager, next"
        :page-sizes="[10, 20, 50]"
        :current-page.sync="page"
        :page-size.sync="limit"
        :total="total"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      ></el-pagination>
      <span class="goto-page">
        前往 <el-input v-model="jumpPage" size="mini" style="width: 50px;" @keyup.enter.native="handleJumpPage"></el-input> 页
      </span>
    </div>

  </div>
</template>

<script>
import Treeselect from '@riophae/vue-treeselect'
import '@riophae/vue-treeselect/dist/vue-treeselect.css'
import { getCoursePage, getCourseCategoryPage } from '@/api/video'

export default {
  name: 'VideoPlace',
  components: { Treeselect },
  data() {
    return {
      keyword: '',
      activeCategory: null,
      categoryOptions: [],
      categoryNormalizer(node) {
        return {
          id: node.id,
          label: node.categoryName,
          children: node.children,
        }
      },
      videoList: [],
      loading: false,
      total: 0,
      page: 1,
      limit: 10,
      jumpPage: '',
    }
  },
  created() {
    this.loadCategories()
    this.loadVideoList();
  },
  watch: {
    activeCategory(val) {
      this.page = 1;
      this.loadVideoList();
    },
  },
  methods: {
    async loadCategories() {
      try {
        const res = await getCourseCategoryPage({ page: 1, limit: 1000 })
        if (res.code === 200 && res.data) {
          const records = (res.data.records || []).filter(item => item.status === 1)
          this.categoryOptions = this.buildCategoryTree(records)
        }
      } catch (e) {
        console.error('加载课程分类失败', e)
      }
    },
    buildCategoryTree(list) {
      const map = {}
      const tree = []
      list.forEach(item => {
        map[item.id] = { ...item }
      })
      list.forEach(item => {
        const node = map[item.id]
        if (!item.parentId || !map[item.parentId]) {
          tree.push(node)
        } else {
          const parent = map[item.parentId]
          if (!parent.children) this.$set(parent, 'children', [])
          parent.children.push(node)
        }
      })
      return tree
    },
    async loadVideoList() {
      this.loading = true;
      try {
        const condition = { courseName: this.keyword, status: 1 }
        if (this.activeCategory) {
          condition.categoryId = Number(this.activeCategory)
        }
        const res = await getCoursePage({ page: this.page, limit: this.limit, condition })
        if (res.code === 200 && res.data) {
          const records = res.data.records || []
          this.videoList = records.map(item => ({
            id: item.id,
            title: item.courseName,
            duration: item.totalDuration ? this.formatDuration(item.totalDuration) : '--',
            categoryName: item.categoryName || '',
            category: String(item.categoryId || ''),
            instructor: item.instructor || '',
            createTime: item.createTime || '',
            coverUrl: item.coverUrl || '',
            description: item.description || '',
            url: '',
          }))
          this.total = res.data.total || 0
        }
      } catch (e) {
        console.error('加载视频列表失败', e)
      } finally {
        this.loading = false
      }
    },
    resolveCoverUrl(url) {
      if (!url) return ''
      if (/^https?:\/\//i.test(url)) return url
      let path = url.startsWith('/') ? url : `/${url}`
      path = path.replace(/^\/home\/file_upload/, '/files')
      const base = (
        this.$store.state.IPAss.yl ||
        process.env.VUE_APP_PREVIEW_FILE_URL ||
        ''
      ).replace(/\/$/, '')
      return `${base}${path}`
    },
    formatDuration(seconds) {
      const m = Math.floor(seconds / 60)
      const s = seconds % 60
      return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    },
    handleSearch() {
      this.page = 1;
      this.loadVideoList();
    },
    handleCategoryChange(node) {
      this.activeCategory = node && node.id != null ? node.id : null
      this.page = 1
      this.loadVideoList()
    },
    handleSizeChange(val) {
      this.page = 1;
      this.limit = val;
      this.loadVideoList();
    },
    handleCurrentChange(val) {
      this.page = val;
      this.loadVideoList();
    },
    handleJumpPage() {
      const p = parseInt(this.jumpPage);
      if (p && p > 0 && p <= Math.ceil(this.total / this.limit)) {
        this.page = p;
        this.loadVideoList();
      }
      this.jumpPage = '';
    },
    handlePlay(item) {
      this.$router.push({
        path: '/video/VideoChapter',
        query: {
          courseId: item.id,
          courseName: item.title,
          courseType: item.categoryName,
          instructor: item.instructor,
          createTime: item.createTime,
          coverUrl: this.resolveCoverUrl(item.coverUrl),
          description: item.description || '',
          readonly: 'true',
        },
      })
    },
  },
}
</script>

<style scoped lang="scss">
.video-place-container {
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 120px);
  box-sizing: border-box;
}

/* 筛选栏 */
.filter-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
  flex-shrink: 0;

  .search-input {
    width: 260px;
  }

  .category-select {
    width: 220px;
  }

  .search-btn {
    flex-shrink: 0;
  }
}

/* 视频卡片网格 */
.video-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
  flex: 1;
  overflow-y: auto;
  align-content: start;
  min-height: 0;

  @media (max-width: 1400px) {
    grid-template-columns: repeat(4, 1fr);
  }
  @media (max-width: 992px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
}

.video-card {
  background: #fff;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  border: 1px solid #ebeef5;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);

    .play-circle {
      transform: translate(-50%, -50%) scale(1.1);
      background: rgba(255, 255, 255, 0.28);
    }
  }

  .video-thumb {
    position: relative;
    width: 100%;
    padding-top: 56%;
    background: linear-gradient(135deg, #b3d8ff 0%, #7fb8ff 50%, #5ea0f0 100%);
    background-size: cover;
    background-position: center;
    overflow: hidden;

    &.has-cover::before {
      content: none;
    }

    .thumb-image {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      background:
        radial-gradient(circle at 20% 25%, rgba(255, 255, 255, 0.35), transparent 45%),
        radial-gradient(circle at 80% 75%, rgba(255, 255, 255, 0.2), transparent 50%);
      pointer-events: none;
    }

    .play-circle {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 70px;
      height: 70px;
      border-radius: 50%;
      background: linear-gradient(135deg, rgba(64, 158, 255, 0.92) 0%, rgba(46, 121, 232, 0.92) 100%);
      backdrop-filter: blur(4px);
      display: flex;
      align-items: center;
      justify-content: center;
      transition: transform 0.25s ease, box-shadow 0.25s ease;
      box-shadow:
        0 6px 20px rgba(46, 121, 232, 0.45),
        inset 0 0 0 1.5px rgba(255, 255, 255, 0.5);
      animation: playPulse 2.4s ease-in-out infinite;

      .play-circle-ring {
        position: absolute;
        inset: -8px;
        border-radius: 50%;
        border: 1.5px solid rgba(255, 255, 255, 0.35);
        pointer-events: none;
      }

      .play-icon {
        font-size: 30px;
        color: #fff;
        margin-left: 4px;
        text-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
      }
    }

    @keyframes playPulse {
      0%, 100% {
        box-shadow:
          0 6px 20px rgba(46, 121, 232, 0.45),
          inset 0 0 0 1.5px rgba(255, 255, 255, 0.5);
      }
      50% {
        box-shadow:
          0 6px 28px rgba(46, 121, 232, 0.65),
          inset 0 0 0 1.5px rgba(255, 255, 255, 0.7);
      }
    }
  }

  .video-info {
    padding: 10px 12px;

    .video-title {
      font-size: 13px;
      color: #303133;
      line-height: 1.4;
      height: 36px;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
      margin-bottom: 8px;
      font-weight: 600;
    }

    .video-meta {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .category-tag {
        background: linear-gradient(135deg, #e6f0ff 0%, #d9e8ff 100%) !important;
        color: #409eff !important;
        border: none !important;
        font-size: 11px !important;
        padding: 1px 8px !important;
        height: 20px !important;
        line-height: 18px !important;
        font-weight: 500;
        border-radius: 3px !important;
      }

      .instructor {
        font-size: 12px;
        color: #909399;
      }
    }

    .video-desc {
      margin-top: 6px;
      font-size: 12px;
      color: #606266;
      line-height: 1.5;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
      word-break: break-all;
    }
  }
}

/* 分页 */
.pagination-wrap {
  margin-top: 16px;
  padding: 12px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: #606266;
  flex-shrink: 0;

  .pagination-info {
    white-space: nowrap;
    margin-right: 20px;
  }

  ::v-deep .el-pagination {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 0;
  }

  ::v-deep .el-pagination .el-pagination__sizes,
  ::v-deep .el-pagination .el-pager,
  ::v-deep .el-pagination .btn-prev,
  ::v-deep .el-pagination .btn-next {
    margin: 0;
  }

  ::v-deep .el-pagination .el-pagination__jump {
    margin-left: 0;
  }

  .goto-page {
    display: inline-flex;
    align-items: center;
    white-space: nowrap;
    margin-left: 20px;

    .el-input {
      margin: 0 4px;
    }
  }
}

.empty-tip {
  grid-column: 1 / -1;
  text-align: center;
  padding: 60px 0;
  color: #c0c4cc;

  i {
    font-size: 48px;
    margin-bottom: 10px;
  }

  p {
    font-size: 14px;
  }
}



</style>
